import { zodResolver } from "@hookform/resolvers/zod";
import { determineAge } from "@jutro/tools";
import { useAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useForm } from "react-hook-form";
import { scheduleVisitOrganizationIdAtom } from "components/ScheduleVisitForm/atoms";
import {
  ScheduleFormSchema,
  getVisitTypeOptions,
  prepareDefaultValue,
  scheduleFormSchema,
  telemedicalVisitTypes,
} from "components/ScheduleVisitForm/form";
import { transformShifts } from "components/ScheduleVisitForm/tools";
import {
  ScheduleVisitTableData,
  ScheduleVisitTileData,
} from "components/ScheduleVisitForm/types";
import {
  DoctorGetVisitDataForEditFormQuery,
  EdmSearchAvailabilityForDayQuery,
  EdmSearchAvailabilityQuery,
  useDoctorGetPatientQuery,
  useEdmSearchAvailabilityForDayLazyQuery,
  useEdmSearchAvailabilityLazyQuery,
} from "lib/graphql/megaSchema";
import { useVojevodshipOptions } from "lib/hooks/useVojevodshipOptions";
import { useGetDoctorOptions } from "views/Visits/PerDay/components/Controls/DoctorSelect/hooks";

type Props = {
  scheduleVisitTileData: ScheduleVisitTileData;
  visitData: DoctorGetVisitDataForEditFormQuery | undefined;
};

export const useScheduleVisitForm = ({
  scheduleVisitTileData,
  visitData,
}: Props) => {
  const { showBoundary } = useErrorBoundary();

  const { patientId, mode } = scheduleVisitTileData;

  const { organizations } = useVojevodshipOptions();

  const { data, loading: isPatientDataLoading } = useDoctorGetPatientQuery({
    fetchPolicy: "cache-first",
    variables: {
      id: patientId,
    },
  });

  const patientData = data?.doctorPatient;
  const patientPesel = patientData?.pesel;
  const patientDeclaration = data?.doctorPatient?.nfzStatus.declaredIn;
  const patientAge = determineAge(patientPesel ?? "");
  const isPatientChild = patientAge < 18 || patientPesel?.length === 12;

  const isViewForceable = mode === "forceEdit" || mode === "forceCreate";

  const [isLoading, setIsLoading] = useState(false);
  const [showNoDeclarationMessageBox, setShowNoDeclarationMessageBox] =
    useState(false);

  const [organizationId, setOrganizationId] = useAtom(
    scheduleVisitOrganizationIdAtom,
  );

  const methods = useForm<ScheduleFormSchema>({
    resolver: zodResolver(scheduleFormSchema),
    defaultValues: prepareDefaultValue(visitData, mode, organizationId),
  });

  const { watch, setValue, clearErrors } = methods;

  useEffect(() => {
    const formValues = prepareDefaultValue(visitData, mode, organizationId);

    Object.entries(formValues).forEach(([key, value]) => {
      setValue(key as keyof ScheduleFormSchema, value, { shouldDirty: false });
    });

    setOrganizationId(formValues.organizationId);
  }, [visitData]);

  useEffect(() => {
    setValue("mode", mode);
    clearErrors();
  }, [mode]);

  const { visitType, staffId } = watch();

  const visitTypeOptions = useMemo(
    () => getVisitTypeOptions(mode, isPatientChild),
    [mode, isPatientChild],
  );

  const isTelemedicalVisitType = Boolean(
    telemedicalVisitTypes.includes(visitType),
  );

  const [getEdmSearchAvailability] = useEdmSearchAvailabilityLazyQuery();

  const [getEdmSearchAvailabilityForDay] =
    useEdmSearchAvailabilityForDayLazyQuery();

  const doctors = useGetDoctorOptions();

  const [availableStaff, setAvailableStaff] = useState<
    EdmSearchAvailabilityQuery["edmSearchAvailability"]["medicalPeople"]
  >([]);

  const doctorOptions = useMemo(
    () =>
      isViewForceable
        ? doctors
        : [
            { label: "Wszyscy", value: null },
            ...doctors.filter(({ value }) =>
              availableStaff.some((staff) => staff.id === value),
            ),
          ],
    [availableStaff, isViewForceable],
  );

  const [shifts, setShifts] = useState<ScheduleVisitTableData[] | null>(null);

  const getShifts = async () => {
    const resolvedOrganizationId = isTelemedicalVisitType
      ? (patientData?.nfzStatus.declaredIn ?? "PM")
      : organizationId;

    const languages = ["pl", "en", "uk"];

    const { data, error } = await getEdmSearchAvailability({
      fetchPolicy: "cache-and-network",
      variables: {
        input: {
          acceptChildren: isPatientChild,
          languages,
          organizationId: resolvedOrganizationId,
          patientId,
          serviceIds: [],
          visitType,
        },
      },
    });

    if (!data || error) {
      showBoundary(error);
      return;
    }

    const { medicalPeople, dates } = data.edmSearchAvailability;

    setAvailableStaff(medicalPeople);

    const allShifts: EdmSearchAvailabilityForDayQuery["edmSearchAvailabilityForDay"] =
      [];

    for (const date of dates) {
      const { data, error } = await getEdmSearchAvailabilityForDay({
        fetchPolicy: "network-only",
        variables: {
          input: {
            acceptChildren: isPatientChild,
            date,
            languages,
            organizationId: resolvedOrganizationId,
            patientId,
            staffId: medicalPeople.some((staff) => staff.id === staffId)
              ? staffId
              : undefined,
            serviceIds: [],
            visitType,
          },
        },
      });

      if (!data || error) {
        showBoundary(error);
        return;
      }

      allShifts.push(...data.edmSearchAvailabilityForDay);
    }

    const transformedShifts = transformShifts(allShifts, medicalPeople);

    setShifts(transformedShifts);
  };

  useEffect(() => {
    (async () => {
      setShowNoDeclarationMessageBox(false);
      setShifts(null);
      setAvailableStaff([]);

      if (isViewForceable || (mode === "edit" && !visitData)) return;

      setIsLoading(true);

      if (!patientDeclaration && isTelemedicalVisitType) {
        setIsLoading(false);
        setShowNoDeclarationMessageBox(true);
        return;
      }

      await getShifts();

      setIsLoading(false);
    })();
  }, [visitType, organizationId, staffId, isViewForceable]);

  const officeOptions = useMemo(() => {
    if (!organizationId) {
      return [];
    }

    return organizations
      .filter(({ id }) => id.includes(organizationId) && id !== organizationId)
      .map(({ displayName, id }) => ({
        label: displayName,
        value: id,
      }));
  }, [organizationId, organizations]);

  const organizationsOptions = useMemo(() => {
    const sortedOrganizations = [...organizations].sort((a, b) => {
      const aCity = a.placeData?.city ?? "a";
      const bCity = b.placeData?.city ?? "a";
      return aCity < bCity ? -1 : 1;
    });

    return sortedOrganizations.map(({ displayName, id, placeData }) => ({
      label: `${displayName}${placeData ? ` (${placeData.city})` : ""}`,
      value: id,
    }));
  }, [organizations]);

  return {
    doctorOptions,
    officeOptions,
    visitTypeOptions,
    organizationsOptions,
    isViewForceable,
    isTelemedicalVisitType,
    showNoDeclarationMessageBox,
    methods,
    shifts,
    isLoading,
    isPatientDataLoading,
  };
};
